import React, { useMemo } from "react";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
} from "@chakra-ui/react";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import type { Clarification } from "../../types/clarification.type";
import { ClarificationSendList } from "./ClarificationSendList";

interface SupplierAccordionPanelProps {
	clarifications: Clarification[];
}

interface SupplierGroup {
	items: Clarification[];
	supplierName: string;
}

const groupBySupplier = (clarifications: Clarification[]): SupplierGroup[] => {
	const map = new Map<string, Clarification[]>();
	clarifications.forEach((c) => {
		const key = c.supplier_name ?? "Unknown supplier";
		const list = map.get(key);
		if (list) {
			list.push(c);
		} else {
			map.set(key, [c]);
		}
	});
	return Array.from(map.entries())
		.map(([supplierName, items]) => ({ supplierName, items }))
		.sort((a, b) => a.supplierName.localeCompare(b.supplierName));
};

export const SupplierAccordionPanel: React.FC<SupplierAccordionPanelProps> = ({
	clarifications,
}) => {
	const groups = useMemo(
		() => groupBySupplier(clarifications),
		[clarifications]
	);

	return (
		<div className="space-y-3">
			<InstructionsInfoBox inline>Below are the clarifications that will be sent to each supplier</InstructionsInfoBox>

			<Accordion allowToggle defaultIndex={0} data-testid="supplier-accordion">
				{groups.map((group) => (
					<AccordionItem
						key={group.supplierName}
						className="!rounded-lg !border !border-gray-200 mb-2 bg-white"
					>
						<AccordionButton
							className="!rounded-lg hover:!bg-gray-50"
							data-testid={`supplier-accordion-button-${group.supplierName}`}
						>
							<Box as="span" flex="1" textAlign="left" className="text-sm font-semibold text-gray-900">
								{group.supplierName}{" "}
								<span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
									{group.items.length}
								</span>
							</Box>
							<AccordionIcon />
						</AccordionButton>
						<AccordionPanel className="!pt-2">
							<ClarificationSendList clarifications={group.items} />
						</AccordionPanel>
					</AccordionItem>
				))}
			</Accordion>
		</div>
	);
};
